import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useWorkspace } from '../lib/workspace';
import { GateShell, GatePill, GateInput, GateNote, friendlyGateError } from './gate/ui';

// ─────────────────────────────────────────────────────────────────────────────
// BUILD A CLAN — GATE flow (Building → Build a clan). One decision per screen:
// name your clan, forge it, move on.
//
// T3 (server-is-the-org, 2026-07-19): the backend closed direct POST /me/clan
// the moment an org context resolves (body orgId, or — if that's absent — the
// JWT's entryOrgId auto-link fallback; see resolveOptionalCreateOrg in
// portalApi/index.ts). Founders/platform admins are the one bypass. So the
// FE branches the same way:
//   - org context + not founder/admin → apply via
//     POST /orgs/:orgId/clan-create-requests, then show pending/denied status
//     from GET /me/clan-create-requests (an org admin approves from the Run It
//     cockpit's "Clan applications" panel).
//   - no org context at all (legacy org-less path) or founder/admin → the
//     original direct-forge POST /me/clan, unchanged.
// ─────────────────────────────────────────────────────────────────────────────

function bareOrgId(id: string): string {
  return id.replace(/^ORG#/, '');
}

export function BuildClanScreen() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const preview = import.meta.env.DEV ? params.get('as') : null;

  const { activeOrgId, activeOrg, entryOrg } = useWorkspace();
  const { data: me, isLoading } = useQuery({ queryKey: ['me'], queryFn: api.me, enabled: !preview });

  const [name, setName] = useState('');
  const [gamertag, setGamertag] = useState('');
  const [description, setDescription] = useState('');
  const [forged, setForged] = useState<string | null>(null);
  const [applied, setApplied] = useState(false);

  // Same org-context resolution the backend performs: body orgId (here,
  // activeOrgId — exactly what createMyClan below sends) first, else the
  // entry org the JWT auto-links. Founders/admins keep the direct path no
  // matter what — mirrors the backend's `role !== 'founder' && role !== 'admin'`
  // bypass exactly.
  const isPlatformBypass = me?.role === 'founder' || me?.role === 'admin';
  const orgContextId = activeOrgId ?? entryOrg?.orgId ?? null;
  const orgContextName = activeOrgId ? (activeOrg?.name ?? bareOrgId(orgContextId ?? '')) : entryOrg?.orgName;
  const requiresApplication = !preview && !isPlatformBypass && !!orgContextId;

  const { data: myRequests, isLoading: myRequestsLoading } = useQuery({
    queryKey: ['myClanCreateRequests'],
    queryFn: api.myClanCreateRequests,
    enabled: requiresApplication,
  });

  // Most recent request against THIS org (a user may hold historical requests
  // against other orgs from a prior workspace context — irrelevant here).
  const existingRequest = orgContextId
    ? [...(myRequests?.requests ?? [])]
        .filter(r => bareOrgId(r.orgId) === bareOrgId(orgContextId))
        .sort((a, b) => b.createdAt - a.createdAt)[0]
    : undefined;

  const createMut = useMutation({
    mutationFn: () => api.createMyClan({ name: name.trim(), gamertag: gamertag.trim() || undefined, description: description.trim() || undefined, orgId: activeOrgId ?? undefined }),
    onSuccess: res => {
      setForged(res.clanName ?? name.trim());
      qc.invalidateQueries({ queryKey: ['me'] });
      qc.invalidateQueries({ queryKey: ['clan'] });
    },
  });

  const applyMut = useMutation({
    mutationFn: () => api.submitClanCreateRequest(orgContextId!, {
      name: name.trim(),
      gamertag: gamertag.trim() || undefined,
      description: description.trim() || undefined,
    }),
    onSuccess: () => {
      setApplied(true);
      setName('');
      setGamertag('');
      setDescription('');
      qc.invalidateQueries({ queryKey: ['myClanCreateRequests'] });
    },
  });

  // DEV preview personas: 'leader' shows the has-clan state, anything else the form.
  const clanName = preview ? (preview === 'leader' ? 'Preview Wolves' : undefined) : me?.clanName;

  if (forged) {
    return (
      <GateShell header="Clan forged:" onBack={() => navigate('/gate')}>
        <p className="settle text-center font-display text-2xl font-bold uppercase tracking-wide text-white">{forged}</p>
        <GateNote idx={1}>You're the leader — invite members from My Clan</GateNote>
        <GatePill label="Join a competition" onClick={() => navigate('/gate/join-comp')} primary idx={2} />
        <GatePill label="My Clan" onClick={() => navigate('/leader')} idx={3} />
        <GatePill label="Dashboard" onClick={() => navigate('/')} idx={4} />
      </GateShell>
    );
  }

  if (!preview && (isLoading || (requiresApplication && myRequestsLoading))) {
    return <GateShell header="Building:" onBack={() => navigate('/gate')}><GateNote idx={2}>…</GateNote></GateShell>;
  }

  if (clanName) {
    return (
      <GateShell header="Building:" onBack={() => navigate('/gate')}>
        <GateNote idx={2}>You already belong to</GateNote>
        <p className="settle text-center font-display text-2xl font-bold uppercase tracking-wide text-white">{clanName}</p>
        <GateNote idx={3}>One clan per player — leave it before starting a new one</GateNote>
        <GatePill label="Join a competition" onClick={() => navigate('/gate/join-comp')} primary idx={4} />
        <GatePill label="My Clan" onClick={() => navigate('/leader')} idx={5} />
      </GateShell>
    );
  }

  // ── Apply-to-create flow — org context, not founder/admin ─────────────────
  if (requiresApplication) {
    const pending = applied || existingRequest?.status === 'PENDING';

    if (pending) {
      return (
        <GateShell header="Building:" onBack={() => navigate('/gate')}>
          <p className="settle text-center font-display text-2xl font-bold uppercase tracking-wide text-white">
            Application sent
          </p>
          <GateNote idx={2}>
            {orgContextName ? `An admin at ${orgContextName} will review it` : 'An org admin will review it'}
          </GateNote>
          {existingRequest && (
            <GateNote idx={3}>
              Applied for "{existingRequest.name}" · {new Date(existingRequest.createdAt).toLocaleDateString()}
            </GateNote>
          )}
          <GatePill label="Dashboard" onClick={() => navigate('/')} primary idx={4} />
        </GateShell>
      );
    }

    const wasDenied = existingRequest?.status === 'DENIED';

    return (
      <GateShell header="Build a clan:" onBack={() => navigate('/gate')}>
        {wasDenied && (
          <GateNote tone="error" idx={1}>
            Your last application was declined{existingRequest?.resolverNote ? `: ${existingRequest.resolverNote}` : ''}.
            You can apply again below.
          </GateNote>
        )}
        <GateInput value={name} onChange={setName} placeholder="Clan name" idx={2} />
        <GateInput value={gamertag} onChange={setGamertag} placeholder="Your gamertag, e.g. Queeni#1652" idx={3} />
        <GateInput value={description} onChange={setDescription} placeholder="Description (optional)" idx={4} multiline />
        {applyMut.isError && <GateNote tone="error" idx={5}>{friendlyGateError((applyMut.error as Error).message)}</GateNote>}
        <GatePill
          label={applyMut.isPending ? 'Applying…' : 'Apply to forge clan'}
          onClick={() => applyMut.mutate()}
          primary
          disabled={!name.trim() || applyMut.isPending}
          idx={6}
        />
        <GateNote idx={7}>
          Goes to {orgContextName ?? 'your org'} for approval · you become leader once approved
        </GateNote>
        {/* Second action on this landing (Lisa, 2026-07-19): the GATE's
            "Apply to join or create a clan" tile lands here for every
            non-leader org member — offer the join-an-existing-clan door too,
            not just create. */}
        <GatePill label="Browse clans to join instead" onClick={() => navigate('/clans/browse')} idx={8} />
      </GateShell>
    );
  }

  // ── Legacy direct-forge flow — no org context at all ───────────────────────
  return (
    <GateShell header="Build a clan:" onBack={() => navigate('/gate')}>
      <GateInput value={name} onChange={setName} placeholder="Clan name" idx={2} />
      <GateInput value={gamertag} onChange={setGamertag} placeholder="Your gamertag, e.g. Queeni#1652" idx={3} />
      <GateInput value={description} onChange={setDescription} placeholder="Description (optional)" idx={4} multiline />
      {createMut.isError && <GateNote tone="error" idx={5}>{friendlyGateError((createMut.error as Error).message)}</GateNote>}
      <GatePill
        label={createMut.isPending ? 'Forging…' : 'Forge clan'}
        onClick={() => createMut.mutate()}
        primary
        disabled={!name.trim() || createMut.isPending}
        idx={6}
      />
      <GateNote idx={7}>You become the clan leader · your gamertag seats you</GateNote>
    </GateShell>
  );
}
